/**
 * ResidentProfile — identity, linked users (max 3), notification prefs,
 * change password (OTP flow), contact support, sign out.
 */
import { useState } from 'react';
import { UserPlus, Trash2, LogOut, MessageCircleQuestion, Trophy, ChevronRight } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';
import { profileDisplayName, accountPosition } from '../../lib/accountIdentity';
import BiometricSettings from '../../components/BiometricSettings';
import { ResidentRewards } from '../../components/rewards/ResidentRewards';

const GOLD = '#C9A962';
const inputCls = 'w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white text-sm focus:border-[#C9A962] focus:outline-none';
const labelCls = 'text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block';

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!on)}
      className={`w-11 h-6 rounded-full relative transition-colors ${disabled ? 'opacity-40' : ''}`}
      style={{ backgroundColor: on ? GOLD : '#3f3f46' }} data-testid={testid}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}

export default function ResidentProfile({ me, onChanged, onLoggedOut, onOpenRecord }) {
  const [lu, setLu] = useState({ name: '', relationship: 'household member' });
  const [pw, setPw] = useState({ step: 0, code: '', current_password: '', new_password: '' });
  const prefs = me.notification_prefs || { in_app: true, sms: true, email: true };

  const addLinked = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/api/resident/linked-users', { method: 'POST', body: lu });
      toast.success(`${lu.name} added`);
      setLu({ name: '', relationship: 'household member' });
      onChanged();
    } catch (err) { toast.error(err.detail || 'Failed to add'); }
  };

  const removeLinked = async (id) => {
    await apiFetch(`/api/resident/linked-users/${id}`, { method: 'DELETE' });
    toast.success('Removed');
    onChanged();
  };

  const setPref = async (k, v) => {
    try {
      await apiFetch('/api/resident/preferences', { method: 'PATCH', body: { [k]: v } });
      onChanged();
    } catch (err) { toast.error(err.detail || 'Update failed'); }
  };

  const startPw = async () => {
    try {
      await apiFetch('/api/auth/change-password/send-otp', { method: 'POST' });
      setPw((s) => ({ ...s, step: 1 }));
      toast.success('Verification code sent to your email');
    } catch (err) { toast.error(err.detail || 'Could not send code'); }
  };

  const confirmPw = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/api/auth/change-password/verify', { method: 'POST', body: {
        code: pw.code, current_password: pw.current_password, new_password: pw.new_password } });
      toast.success('Password changed');
      setPw({ step: 0, code: '', current_password: '', new_password: '' });
    } catch (err) { toast.error(err.detail || 'Change failed'); }
  };

  const logout = async () => {
    try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch (_) { /* ignore */ }
    onLoggedOut();
  };

  return (
    <div className="space-y-6" data-testid="resident-profile-screen">
      {/* Identity */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
        <p className="text-base font-black uppercase text-white break-words" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="resident-profile-name">{profileDisplayName(me)}</p>
        <p className="text-xs text-[#C9A962] mt-1" data-testid="resident-profile-position">{accountPosition(me)}</p>
        <div className="mt-3" data-testid="resident-profile-contact-name"><p className="text-[10px] text-zinc-400">Name</p><p className="text-sm text-white break-words">{profileDisplayName(me) || '—'}</p></div>
        <p className="text-[11px] text-zinc-500 mt-0.5 [overflow-wrap:anywhere]" data-testid="resident-profile-contact-details">{me.email} · {me.phone}</p>
        <p className="text-[11px] mt-1.5"><span style={{ color: GOLD }}>{me.property_name}</span> <span className="text-zinc-500">· Unit {me.unit_number}</span></p>
      </div>

      {/* Main menu */}
      <ResidentRewards prefix="resident-profile-rewards" />
      <BiometricSettings />
      {onOpenRecord && (
        <section data-testid="resident-profile-menu">
          <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Menu</h2>
          <button onClick={onOpenRecord}
            className="w-full flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5"
            data-testid="profile-operation-record-link">
            <span className="flex items-center gap-2 text-sm font-bold text-white">
              <Trophy className="h-4 w-4" style={{ color: GOLD }} /> Operation Record
            </span>
            <ChevronRight className="h-4 w-4 text-zinc-600" />
          </button>
        </section>
      )}

      {/* Authorizer — visible only to the primary account holder.
          Iter-182 · Phase 6 — also hidden for authorizer users
          (is_authorizer=true) even if is_primary_holder somehow slips
          through, so A/U dashboards never expose the invite flow. */}
      {me.is_primary_holder && !me.is_authorizer && (
      <section data-testid="resident-authorizer-section">
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>
          Linked Users · {(me.linked_users || []).length}/3
        </h2>
        <div className="space-y-2" data-testid="resident-linked-users-list">
          {(me.linked_users || []).map((u) => (
            <div key={u.linked_id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 flex items-center justify-between" data-testid={`linked-user-${u.linked_id}`}>
              <div>
                <p className="text-sm font-bold text-white">{u.name}</p>
                <p className="text-[10px] text-zinc-500 capitalize">{u.relationship}</p>
              </div>
              <button onClick={() => removeLinked(u.linked_id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`linked-user-remove-${u.linked_id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        {(me.linked_users || []).length < 3 && (
          <form onSubmit={addLinked} className="flex gap-2 mt-2">
            <input required minLength={2} placeholder="Name" className={inputCls}
              value={lu.name} onChange={(e) => setLu((s) => ({ ...s, name: e.target.value }))} data-testid="linked-user-name-input" />
            <select className={`${inputCls} w-36`} value={lu.relationship}
              onChange={(e) => setLu((s) => ({ ...s, relationship: e.target.value }))} data-testid="linked-user-relationship">
              <option value="household member">Household</option>
              <option value="spouse/partner">Spouse</option>
              <option value="authorized occupant">Occupant</option>
              <option value="caregiver">Caregiver</option>
            </select>
            <button type="submit" className="px-3 rounded-lg shrink-0" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="linked-user-add-btn">
              <UserPlus className="h-4 w-4" />
            </button>
          </form>
        )}
      </section>
      )}

      {/* Notification prefs */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Notification Preferences</h2>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 divide-y divide-zinc-800">
          {[
            { k: 'in_app', label: 'In-App Alerts', note: 'Always on', locked: true },
            { k: 'sms', label: 'SMS Text Messages', note: `To ${me.phone}` },
            { k: 'email', label: 'Email', note: `To ${me.email}` },
          ].map(({ k, label, note, locked }) => (
            <div key={k} className="flex items-center justify-between p-3.5">
              <div>
                <p className="text-sm font-bold text-white">{label}</p>
                <p className="text-[10px] text-zinc-500">{note}</p>
              </div>
              <Toggle on={locked ? true : prefs[k] !== false} disabled={locked}
                onChange={(v) => setPref(k, v)} testid={`pref-toggle-${k}`} />
            </div>
          ))}
        </div>
        <p className="text-[10px] text-zinc-600 mt-1.5">Violation status updates are always delivered and can't be turned off.</p>
      </section>

      {/* Change password */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Security</h2>
        {pw.step === 0 ? (
          <button onClick={startPw} className="w-full py-3 rounded-xl border border-zinc-700 text-sm font-bold text-zinc-300" data-testid="change-password-start-btn">
            Change Password
          </button>
        ) : (
          <form onSubmit={confirmPw} className="space-y-2.5 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
            <div><label className={labelCls}>Email verification code</label>
              <input required className={inputCls} value={pw.code} onChange={(e) => setPw((s) => ({ ...s, code: e.target.value }))} data-testid="pw-otp-input" /></div>
            <div><label className={labelCls}>Current password</label>
              <input type="password" required className={inputCls} value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} data-testid="pw-current-input" /></div>
            <div><label className={labelCls}>New password (min 8)</label>
              <input type="password" required minLength={8} className={inputCls} value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} data-testid="pw-new-input" /></div>
            <button type="submit" className="w-full py-2.5 rounded-full font-black uppercase tracking-widest text-xs" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="pw-confirm-btn">
              Update Password
            </button>
          </form>
        )}
      </section>

      {/* Support */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Support</h2>
        <a href="/help-desk" className="flex items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5 text-sm text-zinc-300" data-testid="support-chat-link">
          <MessageCircleQuestion className="h-4 w-4" style={{ color: GOLD }} /> Chat with Noble AI
        </a>
        <a href="mailto:support@nobleempirecs.com" className="block mt-2 text-center text-[11px] text-zinc-500" data-testid="support-email-link">
          support@nobleempirecs.com
        </a>
      </section>

      <button onClick={logout} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-900/60 text-sm font-bold text-red-400" data-testid="resident-logout-btn">
        <LogOut className="h-4 w-4" /> Sign Out
      </button>
    </div>
  );
}
